import { Interface } from 'ethers/lib/utils'
import { MERKLE_TIME_RELEASE_ABI, TIME_RELEASE_ABI, TIME_RELEASE_ADDRESS } from '../../../constants'
import { useChainCalls, useEthers } from '../../../hooks'

type Account = string | null
const releaseCoder = new Interface(TIME_RELEASE_ABI)
const merkleReleaseCoder = new Interface(MERKLE_TIME_RELEASE_ABI)

export function useMerkleClaim(timeReleaseAddress: string) {
  const { account } = useEthers()
  const [option1, option2] = useTimeReleaseOptions(merkleReleaseCoder, timeReleaseAddress, account)
  const { claimed: claimed1 } = option1
  const { claimed: claimed2 } = option2

  return {
    option1Claimed: claimed1,
    option2Claimed: claimed2,
  }
}

export function useClaim() {
  const { account, chainId } = useEthers()
  const [option1, option2] = useTimeReleaseOptions(releaseCoder, chainId && TIME_RELEASE_ADDRESS[chainId], account)
  const { claimed: claimed1, allocation: allocation1 } = option1
  const { claimed: claimed2, allocation: allocation2 } = option2
  const [option1Claimable, option2Claimable] = useClaimable(
    releaseCoder,
    chainId && TIME_RELEASE_ADDRESS[chainId],
    account
  )

  return {
    option1Total: allocation1,
    option2Total: allocation2,
    option1Claimed: claimed1,
    option2Claimed: claimed2,
    option1Claimable,
    option2Claimable,
  }
}

const emptyOption = { claimed: undefined, allocation: undefined }

function useTimeReleaseOptions(coder: Interface, timeReleaseAddress?: string, account?: Account) {
  const calls = getCalls(coder, 'option1', 'option2', timeReleaseAddress, account)
  const results = useChainCalls(calls)
  if (results.length > 0) {
    return results.map((result, i) => (result ? calls[i].decode(result) : emptyOption))
  }
  return [emptyOption, emptyOption]
}

function useClaimable(coder: Interface, timeReleaseAddress?: string, account?: Account) {
  const calls = getCalls(coder, 'getClaimableOption1', 'getClaimableOption2', timeReleaseAddress, account)
  const results = useChainCalls(calls)
  return results.length > 0 ? results.map((result, i) => result && calls[i].decode(result)[0]) : [undefined, undefined]
}

function getCalls(
  coder: Interface,
  option1Function: string,
  option2Function: string,
  timeReleaseAddress?: string,
  account?: Account
) {
  if (account && timeReleaseAddress) {
    return [
      {
        address: timeReleaseAddress!,
        data: coder.encodeFunctionData(option1Function, [account]),
        decode: (data: string) => coder.decodeFunctionResult(option1Function, data),
      },
      {
        address: timeReleaseAddress!,
        data: coder.encodeFunctionData(option2Function, [account]),
        decode: (data: string) => coder.decodeFunctionResult(option2Function, data),
      },
    ]
  }
  return []
}
